import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Button } from '@els/els-react--button';
import { TogglePanel, TogglePanelCollection } from '@els/els-react--toggle-panel';
import SubstanceDruglikenessComponent from '@Components/Results/substances/substance-druglikeness.component';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import { resultsConfig } from '@Constants/Results';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import SubstanceIdentificationData from '@Components/Results/substances/SubstanceIdentificationData';
import SubstanceSubProperty from '@Components/Results/substances/SubstanceSubProperty';
import getSubstanceSubPropertyId from '@Components/Results/substances/SubstanceSubProperty.utils';
import SubstanceBioData from '@Components/Results/substances/substance-bio-data.component';
import {
  getChildCount,
  getChildHitCount,
  getFactCount,
  isChildCommercialSuppliers,
} from '@Components/Results/substances/utils/factCountHelper';
import { COMMERCIAL_SUPPLIERS_FACT_ID, HIT_DATA_FACT_ID } from '@Constants';
import Slugify from '@Utils/Slugify';

import './SubstanceProperty.scss';
import './SubstanceSubProperty.scss';

const childView = resultsConfig.INITIAL_ITEMS_AMOUNT.substanceProperty.children;

function SubstanceProperty({ metadata, substance, fact, details, database, substanceIndex, zoom, isTabPanel = false }) {
  const [childLimit, setChildLimit] = useState(childView);
  const toggleHandlersRef = useRef({});

  const spinnerId = useMemo(() => `tab-${Slugify(fact.title)}-${substanceIndex}`, [fact.title, substanceIndex]);

  const showMore = () => setChildLimit((limit) => limit + childView);
  const getChildKey = (child) => child.id || child.title || child.extendedTitle || child.format;
  const registerToggleHandler = (panelId) => (handler) => {
    if (handler) {
      toggleHandlersRef.current[panelId] = handler;
    } else {
      delete toggleHandlersRef.current[panelId];
    }
  };
  const renderChildHeader = (child) => {
    const childCount = getChildCount(child, substance);

    return (
      <>
        {child.title}
        {(child.count > 0 || child.size > 0) && (
          <span>
            {' '}
            -&nbsp;
            {fact.id === HIT_DATA_FACT_ID && <span>{getChildHitCount(child)} hits out of </span>}
            {childCount}
          </span>
        )}
      </>
    );
  };

  const renderShowMoreButton = () => (
    <div className="els-btn-group-center">
      <Button className="e2e-load-more" data-e2e="load-more" type="tertiary" size="small" onClick={showMore}>
        Load More
      </Button>
    </div>
  );

  const { children = [], title = '', type, id: factId, drugData } = fact;
  const count = getFactCount(fact, substance);
  const isCommercialSupplierFact = factId === COMMERCIAL_SUPPLIERS_FACT_ID;
  const isHitDataFact = factId === HIT_DATA_FACT_ID;
  const isIdentificationFact = factId === 'ID';
  const visibleChildren = useMemo(() => children.filter((child) => child.id !== 'SEQ'), [children]);
  const displayedChildren = useMemo(() => visibleChildren.slice(0, childLimit), [visibleChildren, childLimit]);
  const hasHitDataCommercialSupplierChild = isTabPanel && isHitDataFact && visibleChildren.some(isChildCommercialSuppliers);
  const hasCompactAccordions =
    isTabPanel &&
    type !== 'category' &&
    type !== 'calc' &&
    !isIdentificationFact &&
    !isCommercialSupplierFact &&
    !hasHitDataCommercialSupplierChild;
  const nestedAccordionClassName = classnames('substance-tab-content__nested-accordions', {
    'substance-tab-content__nested-accordions--commercial-suppliers': isCommercialSupplierFact,
    'substance-tab-content__nested-accordions--hit-data-commercial-suppliers': hasHitDataCommercialSupplierChild,
    'substance-tab-content__nested-accordions--compact': hasCompactAccordions,
  });
  const renderSubstanceSubProperty = (child) => {
    const isHitDataCommercialSupplierChild = hasHitDataCommercialSupplierChild && isChildCommercialSuppliers(child);

    return (
      <SubstanceSubProperty
        key={getChildKey(child)}
        substance={substance}
        details={details}
        metadata={metadata}
        child={child}
        fact={fact}
        substanceIndex={substanceIndex}
        database={database}
        substanceId={details.id}
        isDefaultOpen={factId === COMMERCIAL_SUPPLIERS_FACT_ID}
        isEmbeddedCommercialSupplier={isHitDataCommercialSupplierChild}
        isTabPanel={isHitDataCommercialSupplierChild}
        commercialSupplierCount={factId === COMMERCIAL_SUPPLIERS_FACT_ID ? count : undefined}
      />
    );
  };
  const renderNestedAccordions = () => {
    if (hasCompactAccordions) {
      return (
        <TogglePanelCollection
          className={nestedAccordionClassName}
          headerLevel="4"
          noBottomKeyline
          noTopKeyline
          data-e2e="substance-sub-property-toggle-panel-collection">
          {displayedChildren.map((child) => {
            const panelId = getSubstanceSubPropertyId(fact, child, substanceIndex);

            return (
              <TogglePanel
                key={getChildKey(child)}
                id={panelId}
                data-e2e="substance-sub-property-toggle-panel"
                header={renderChildHeader(child)}
                onClick={(id, openState) => toggleHandlersRef.current[id]?.({ targetOpen: openState })}>
                <SubstanceSubProperty
                  substance={substance}
                  details={details}
                  metadata={metadata}
                  child={child}
                  fact={fact}
                  substanceIndex={substanceIndex}
                  database={database}
                  substanceId={details.id}
                  isTogglePanelContent
                  panelId={panelId}
                  registerToggleHandler={registerToggleHandler(panelId)}
                />
              </TogglePanel>
            );
          })}
        </TogglePanelCollection>
      );
    }

    return <div className={nestedAccordionClassName}>{displayedChildren.map(renderSubstanceSubProperty)}</div>;
  };

  const content = (
    <>
      <RxSpinnerComponent spinnerId={spinnerId} position="relative" />

      <div
        className={classnames('substance-tab-content', {
          'substance-tab-content--commercial-suppliers': isCommercialSupplierFact,
          'substance-tab-content--hit-data-commercial-suppliers': hasHitDataCommercialSupplierChild,
          'substance-tab-content--identification': isIdentificationFact,
          'substance-tab-content--compact-accordions': hasCompactAccordions,
        })}>
        {type !== 'category' && type !== 'calc' && (
          <div className="substance-tab-content__children">
            {factId === 'ID' && <SubstanceIdentificationData metadata={metadata} details={details} database={database} zoom={zoom} />}
            {displayedChildren.length > 0 && renderNestedAccordions()}
            {visibleChildren.length > childLimit && renderShowMoreButton()}
          </div>
        )}

        {type === 'calc' && drugData && <SubstanceDruglikenessComponent drugData={drugData} />}

        {metadata.heatmapMode &&
          type === 'category' &&
          (fact.bioData ? (
            <SubstanceBioData metadata={metadata} fact={fact} substanceId={details.id} />
          ) : (
            <SimpleSpinner className="rx-loader--container__relative" />
          ))}
      </div>
    </>
  );

  if (isTabPanel) {
    return content;
  }

  return (
    <div className="substance-accordion-item">
      <h3 className="substance-accordion-item__title u-els-font-size-body-large u-els-margin-top-1x u-els-margin-bottom-1x">
        {title} {count ? <span className="countLabel">- {count}</span> : null}
      </h3>
      {content}
    </div>
  );
}

SubstanceProperty.propTypes = {
  metadata: MetadataPropTypesShape,
  substance: PropTypes.object.isRequired,
  fact: PropTypes.object.isRequired,
  details: PropTypes.object.isRequired,
  database: PropTypes.object.isRequired,
  substanceIndex: PropTypes.string.isRequired,
  zoom: PropTypes.number,
  isTabPanel: PropTypes.bool,
};

export default SubstanceProperty;
